<template>
  <div class="bg-white border rounded composer">
    <el-input
      v-model="message"
      type="textarea"
      resize="none"
      :autosize="AUTOSIZE"
      :placeholder="t('aichat.main.composer.placeholder')"
      :disabled="disabled"
      :maxlength="4000"
      class="composer-textarea [--el-input-border-color:transparent] [--el-input-hover-border-color:transparent] [--el-input-focus-border-color:transparent] [--el-disabled-bg-color:transparent]"
      data-testid="ai-chat-composer-textarea"
      @keydown="onKeydown"
    />
    <div class="flex flex-row items-center gap-2 px-2 pb-2">
      <el-tooltip
        :content="t('aichat.main.composer.more_prompts')"
        placement="top-start"
        :show-arrow="false"
      >
        <el-button
          class="composer-button more-prompts-button"
          :aria-label="t('aichat.main.composer.more_prompts')"
          data-testid="ai-chat-composer-prompts-button"
          @click="emit('browse')"
        >
          <chat-icon />
        </el-button>
      </el-tooltip>

      <el-popover
        :disabled="!hasCredits"
        trigger="hover"
        placement="top-start"
        :width="260"
        :show-arrow="false"
      >
        <template #reference>
          <!-- the credits button is hidden until the credits are wired up -->
          <el-button
            link
            class="flex items-center credits-icon-btn"
            :aria-label="t('aichat.main.composer.credits.left')"
            data-testid="ai-chat-composer-credits-button"
          >
            <sparkle-icon class="text-th-primary shrink-0" />
            <span v-if="isCreditsLabelShown">
              {{
                t('aichat.main.composer.credits.label', {
                  count: creditsLeftText
                })
              }}
            </span>
          </el-button>
        </template>

        <div class="flex items-baseline mb-2">
          <span class="text-lg font-bold text-th-secondary credits-left">
            {{ creditsLeftText }}
          </span>
          <span class="text-xs text-th-secondary-gray ml-1">
            {{ t('aichat.main.composer.credits.left') }}
          </span>
        </div>
        <div class="w-full rounded overflow-hidden mb-2 credits-track">
          <div
            class="h-full rounded credits-fill"
            :class="{ 'is-low': isCreditsLow }"
            :style="{ width: `${creditsPercent}%` }"
          />
        </div>
        <div class="text-th-secondary-gray credits-description">
          {{
            t('aichat.main.composer.credits.description', {
              limit: creditsLimitText
            })
          }}
          <el-button
            link
            type="primary"
            class="text-th-primary font-bold hover:underline request-more-button"
            data-testid="ai-chat-composer-request-credits-button"
            @click="emit('request-credits')"
          >
            {{ t('aichat.main.composer.credits.request_more') }}
          </el-button>
        </div>
      </el-popover>

      <div class="grow" />

      <el-button
        v-if="isStreaming"
        class="composer-button stop-button"
        :aria-label="t('aichat.main.composer.stop')"
        data-testid="ai-chat-composer-stop-button"
        @click="emit('stop')"
      >
        <video-pause-icon />
      </el-button>
      <el-button
        v-else
        type="primary"
        class="composer-button send-button"
        :aria-label="t('aichat.main.composer.send')"
        :disabled="disabled"
        data-testid="ai-chat-composer-send-button"
        @click="send"
      >
        <arrow-up-line-icon />
      </el-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ArrowUpLineIcon,
  ChatIcon,
  SparkleIcon,
  VideoPauseIcon
} from '@shared/assets/icons'
import { useFormatNumber } from '@shared/plugins/number-formatter'

/* 4 to 10 rows of 21 px: the 84 px to 220 px the composer grew between before */
const AUTOSIZE = { minRows: 4, maxRows: 10 }

const props = withDefaults(
  defineProps<{
    /* null means the merchant is not on a limited plan */
    creditsLeft?: number | null
    creditsLimit?: number | null
    /* an answer is streaming: the send button becomes a stop button */
    isStreaming?: boolean
    /* the chat cannot be used, e.g. the account lacks the agent_chat feature */
    disabled?: boolean
  }>(),
  {
    creditsLeft: null,
    creditsLimit: null,
    isStreaming: false,
    disabled: false
  }
)

const emit = defineEmits<{
  send: [text: string]
  stop: []
  browse: []
  'request-credits': []
}>()

const { t, locale } = useI18n()
const formatNumber = useFormatNumber()

const message = ref('')

const hasCredits = computed(() => props.creditsLeft !== null)

const creditsPercent = computed(() => {
  if (!props.creditsLimit || props.creditsLeft === null) return 0
  const percent = (props.creditsLeft / props.creditsLimit) * 100
  return Math.max(0, Math.min(100, percent))
})

/* the count only appears at or below half the quota, as a heads-up before it runs out */
const isCreditsLabelShown = computed(
  () => hasCredits.value && creditsPercent.value <= 50
)
const isCreditsLow = computed(() => creditsPercent.value <= 20)

const formatCount = (value: number | null): string =>
  value === null
    ? ''
    : formatNumber(value, { locale: locale.value, precision: 0 })

const creditsLeftText = computed(() => formatCount(props.creditsLeft))
const creditsLimitText = computed(() => formatCount(props.creditsLimit))

/* Enter while an answer streams keeps the draft: only one turn runs at a time */
const send = (): void => {
  const text = message.value.trim()
  if (!text || props.isStreaming || props.disabled) return
  emit('send', text)
  message.value = ''
}

/* Enter that confirms an IME candidate (isComposing, or keyCode 229 in Safari) must not send */
const onKeydown = (event: Event | KeyboardEvent): void => {
  if (!(event instanceof KeyboardEvent) || event.key !== 'Enter') return
  if (event.isComposing || event.keyCode === 229) return
  if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return
  event.preventDefault()
  send()
}
</script>

<style scoped>
/* not the `flex` utility: it compiles to `display: flex !important`, which overrides a parent's v-show */
.composer {
  display: flex;
  flex-direction: column;
}

.composer:focus-within {
  border-color: var(--primary-color);
}

.composer-textarea {
  width: 100%;
  min-height: 5.25rem;
  max-height: 13.75rem;
  padding: 0.75rem 1rem 0.25rem;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  box-sizing: border-box;
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-y: auto;
}

.composer-textarea::placeholder {
  color: var(--el-text-color-secondary);
}

.composer-button {
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border-radius: 0.25rem;
}

/* the global `.el-button svg` rule fills icons, which turns these stroke-only icons into solid shapes */
.composer-button svg {
  width: 1rem;
  height: 1rem;
  fill: none;
}

.more-prompts-button {
  --el-button-border-color: var(--el-border-color);
  --el-button-text-color: var(--el-text-color-secondary);
  --el-button-hover-border-color: var(--primary-color);
  --el-button-hover-text-color: var(--primary-color);
  --el-button-hover-bg-color: var(--el-bg-color);
}

/* el-button adds a left margin to a button that follows another button */
.composer-button + .composer-button {
  margin-left: 0;
}

.credits-icon-btn {
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0;
  border: none;
  background: none;
  color: var(--el-text-color-secondary);
  font-size: 0.8125rem;
  cursor: pointer;
  display: none !important; /* credits button hidden temporary */
}

.credits-icon-btn:hover {
  color: var(--secondary-color);
}

.credits-icon-btn svg {
  width: 1.0625rem;
  height: 1.0625rem;
}

.credits-track {
  height: 0.375rem;
  background: var(--light-blue);
}

.credits-fill {
  background: var(--primary-color);
  transition: width 1.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.credits-fill.is-low {
  background: var(--el-color-warning);
}

.credits-description,
.request-more-button {
  font-size: 0.71875rem;
  line-height: 1.5;
}

.request-more-button {
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}
</style>
